/**
 * useGridSelection — checkbox selection for both grid modes, one hook.
 *
 *   ┌ mode="paged" ─────────────────────────────────────────────────┐
 *   │ MUI owns the VISIBLE page's model. The ACCUMULATOR owns the   │
 *   │ rest, so a row picked on page 1 survives a walk to page 4:    │
 *   │                                                               │
 *   │   selectionModel            ─ ids MUI is showing as ticked    │
 *   │   state.selectedRows        ─ every row picked on every page  │
 *   │   keepNonExistentRowsSelected keeps off-page ticks alive      │
 *   └───────────────────────────────────────────────────────────────┘
 *   ┌ mode="local" ─────────────────────────────────────────────────┐
 *   │ MUI owns selection outright — the hook forwards the call      │
 *   │ site's own model — UNLESS enableTreeSelection, where ticking  │
 *   │ a parent ticks its children and unticking a child unticks     │
 *   │ the parent.                                                   │
 *   └───────────────────────────────────────────────────────────────┘
 *
 * Both bodies are moved VERBATIM from `DataGrid.tsx`; the accumulate/prune
 * order, the `==` id comparison and the auto-generated-parent id format are
 * load-bearing and are not re-derived here.
 */
import type {
  DataGridPremiumProps,
  GridGroupNode,
  GridRowId,
  GridRowSelectionModel,
  GridRowSelectionPropagation,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { BaseGridStateWithHelpers } from '../shared/baseGridTypes';

export type GridSelectionMode = 'local' | 'paged';

/** MUI's own prop types, borrowed so the hook never re-declares the grid's contract. */
type GridApiRefProp = DataGridPremiumProps['apiRef'];
type GridRowIdGetterProp = DataGridPremiumProps['getRowId'];
type GridTreeDataPathProp = DataGridPremiumProps['getTreeDataPath'];
type GridSelectionModelChange = NonNullable<DataGridPremiumProps['onRowSelectionModelChange']>;

type SharedGridSelectionOptions = {
  /** Renders the checkbox column and feeds the accumulation into grid state. */
  enableSelection: boolean;
  /** The grid state that owns the accumulated `selectedRows` map. */
  enhancedGridState: BaseGridStateWithHelpers;
  /**
   * REQUIRED deliberately — paged passes the CURRENT page's rows, local the rows AFTER the
   * in-browser filter; both bodies index it unguarded, and guarding here would turn an existing
   * throw into a silently empty selection.
   */
  rows: readonly GridValidRowModel[];
  getRowId?: GridRowIdGetterProp;
};

export type PagedGroupRowsResolver = (groupNode: GridGroupNode) => Promise<readonly GridValidRowModel[]>;
export type PagedAllRowsResolver = () => Promise<readonly GridValidRowModel[]>;
export type PagedRowGroupKeyGetter = (row: GridValidRowModel) => GridGroupNode['groupingKey'];

export type PagedGridSelectionOptions = SharedGridSelectionOptions & {
  mode: 'paged';
  apiRef?: GridApiRefProp;
  resolveGroupRows?: PagedGroupRowsResolver;
  resolveAllRows?: PagedAllRowsResolver;
  getRowGroupKey?: PagedRowGroupKeyGetter;
  selectionScopeKey?: string;
};

export type LocalGridSelectionOptions = SharedGridSelectionOptions & {
  mode: 'local';
  enableTreeSelection: boolean;
  apiRef?: GridApiRefProp;
  getTreeDataPath?: GridTreeDataPathProp;
  /** Used verbatim when tree selection is off — the call site keeps its own model. */
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: GridSelectionModelChange;
};

export type GridSelectionOptions = PagedGridSelectionOptions | LocalGridSelectionOptions;

type SelectionSnapshot = { model: GridRowSelectionModel; rows: Record<string, unknown> };

type PendingGroupBatch = { groupIds: Set<GridRowId> };

type PendingAllRowsRequest = SelectionSnapshot & { requestId: number; scopeKey: string | undefined };

function applySelectionDelta(
  snapshot: GridRowSelectionModel,
  pending: GridRowSelectionModel,
  incoming: GridRowSelectionModel
): GridRowSelectionModel {
  if (snapshot.type !== 'include' || pending.type !== 'include' || incoming.type !== 'include') return incoming;

  const ids = new Set(snapshot.ids);
  for (const id of pending.ids) {
    if (!incoming.ids.has(id)) ids.delete(id);
  }
  for (const id of incoming.ids) {
    if (!pending.ids.has(id)) ids.add(id);
  }
  return { type: 'include', ids };
}

/**
 * The four MUI props this capability owns. Destructured explicitly at the call
 * site — see `selection/index.ts` for the disjoint-props declaration.
 */
export type GridSelectionProps = {
  checkboxSelection: boolean;
  disableRowSelectionExcludeModel: true;
  keepNonExistentRowsSelected: true;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: GridSelectionModelChange;
  rowSelectionPropagation?: GridRowSelectionPropagation;
};

/**
 * MUI's `keepNonExistentRowsSelected` holds ids from pages the user has left inside its model,
 * so prune exactly what the model no longer lists and then add what it gained. Break either
 * half and a paged selection silently forgets every page but the visible one.
 */
export function accumulateSelectedRows(
  previous: Record<string, unknown>,
  newRowIds: GridRowSelectionModel,
  rows: readonly GridValidRowModel[],
  getRowId?: GridRowIdGetterProp
): Record<string, unknown> {
  const selectedRows = { ...previous };

  // Hoisted out of the per-row callback; the `==` scan itself stays. Loose equality cannot be
  // replaced by a canonical-key Set: String keys miss cross-type matches ('01' == 1), and Number
  // keys wrongly join distinct strings ('01' != '1'). Not an equivalence relation, so not hashable.
  const modelIds = Array.from(newRowIds.ids);
  const modelContains = (rowId: GridRowId) => modelIds.some((id) => id == rowId);
  const isSelected = (rowId: GridRowId) =>
    newRowIds.type === 'include' ? modelContains(rowId) : !modelContains(rowId);

  for (const rowId in selectedRows) {
    if (!isSelected(rowId)) {
      delete selectedRows[rowId];
    }
  }

  const selectedIds =
    newRowIds.type === 'include'
      ? newRowIds.ids
      : new Set(rows.map((row) => getRowId?.(row) ?? row.id).filter((rowId) => isSelected(rowId)));

  selectedIds.forEach((id) => {
    if (!selectedRows[id]) {
      const row = rows.find((row) => (getRowId?.(row) ?? row.id) === id);
      if (row) {
        selectedRows[id] = row;
      }
    }
  });

  return selectedRows;
}

type TreeSelectionInput = {
  previousModel: GridRowSelectionModel;
  newModel: GridRowSelectionModel;
  /** The rows AFTER the in-browser filter — expansion walks what the grid shows. */
  rows: readonly GridValidRowModel[];
  /** `apiRef.current.getRow` — MUI answers `null` for an id it does not hold. */
  getRow: (id: GridRowId) => GridValidRowModel | null | undefined;
  getTreeDataPath: (row: GridValidRowModel) => readonly string[];
  getRowId?: GridRowIdGetterProp;
};

/**
 * mode="local", tree — ticking a parent ticks every child under it; unticking a
 * child drops the parent back out. MUI names group rows
 * `auto-generated-row-null/<Group>`, and that format is load-bearing on both sides.
 */
export function resolveTreeSelection({
  previousModel,
  newModel,
  rows,
  getRow,
  getTreeDataPath,
  getRowId,
}: TreeSelectionInput): { selectionModel: GridRowSelectionModel; selectedRows: Record<string, unknown> } {
  // An exclude model means "everything but these". Reading its ids as the selection would invert
  // it, so normalise both models to concrete included ids before any set logic runs.
  const toIncludedIds = (model: GridRowSelectionModel): Set<GridRowId> =>
    model.type === 'include'
      ? model.ids
      : new Set(rows.map((row) => getRowId?.(row) ?? row.id).filter((rowId) => !model.ids.has(rowId)));

  const oldSelectionSet = toIncludedIds(previousModel);
  const newSelectionSet = toIncludedIds(newModel);

  // Build a map of which rows are selected
  const selectedRowsMap: Record<string, unknown> = {};
  const finalSelectionModel: GridRowId[] = [];
  const parentsToRemove = new Set<GridRowId>();

  // First, detect if any children were deselected
  oldSelectionSet.forEach((oldRowId) => {
    if (!newSelectionSet.has(oldRowId) && typeof oldRowId === 'number') {
      const row = getRow(oldRowId);
      if (row) {
        const treePath = getTreeDataPath(row);
        if (treePath.length === 2) {
          // This is a child row that was deselected - mark its parent for removal
          // MUI generates parent IDs in format: auto-generated-row-null/GroupName
          const parentPath = treePath.slice(0, -1).join('/');
          const parentId = `auto-generated-row-null/${parentPath}`;
          parentsToRemove.add(parentId);
        }
      }
    }
  });

  // Process each selected row — the NORMALISED set, not `newModel.ids`, which for an exclude
  // model holds the rows that are NOT selected.
  newSelectionSet.forEach((rowId) => {
    const isAutoGeneratedRow = typeof rowId === 'string' && rowId.startsWith('auto-generated-row');

    if (isAutoGeneratedRow) {
      // Check if this is a newly selected parent (wasn't in previous selection)
      const isNewlySelected = !oldSelectionSet.has(rowId);

      if (isNewlySelected) {
        // Parent was just selected - add all its children
        const groupName = rowId.split('/').pop();
        const childRows = rows.filter((r) => {
          const treePath = getTreeDataPath(r);
          return treePath.length === 2 && treePath[0] === groupName;
        });

        childRows.forEach((child) => {
          const childId = getRowId ? getRowId(child) : child.id;
          selectedRowsMap[childId] = child;
          finalSelectionModel.push(childId);
        });

        // Keep parent in selection model
        finalSelectionModel.push(rowId);
      } else {
        // Parent was already selected - keep it selected but don't re-add children
        // This allows individual children to be deselected
        finalSelectionModel.push(rowId);
      }
    } else {
      // Regular data row
      const row = getRow(rowId);
      if (row) {
        selectedRowsMap[rowId] = row;
        finalSelectionModel.push(rowId);
      }
    }
  });

  // Remove parents that should be deselected due to child deselection
  return {
    selectionModel: {
      type: 'include',
      ids: new Set(finalSelectionModel.filter((rowId) => !parentsToRemove.has(rowId))),
    },
    selectedRows: selectedRowsMap,
  };
}

export function useGridSelection(options: GridSelectionOptions): GridSelectionProps {
  const { enableSelection, enhancedGridState, rows, getRowId } = options;
  const enableTreeSelection = options.mode === 'local' ? options.enableTreeSelection : false;
  const apiRef = options.apiRef;
  const getTreeDataPath = options.mode === 'local' ? options.getTreeDataPath : undefined;

  const resolveGroupRows = options.mode === 'paged' ? options.resolveGroupRows : undefined;
  const resolveAllRows = options.mode === 'paged' ? options.resolveAllRows : undefined;
  const getRowGroupKey = options.mode === 'paged' ? options.getRowGroupKey : undefined;
  const selectionScopeKey = options.mode === 'paged' ? options.selectionScopeKey : undefined;
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(() => ({
    type: 'include',
    ids: new Set(),
  }));
  const selectionModelRef = useRef(selectionModel);
  const selectedRowsRef = useRef(enhancedGridState.selectedRows);
  const lastExternalSelectedRowsRef = useRef(enhancedGridState.selectedRows);
  const groupRowsRef = useRef(new Map<GridRowId, Map<GridRowId, GridValidRowModel>>());
  const pendingGroupRequestsRef = useRef(new Map<GridRowId, number>());
  const pendingGroupBatchesRef = useRef(new Map<number, PendingGroupBatch>());
  const pendingAllRowsRequestRef = useRef<PendingAllRowsRequest>();
  const resolvedAllRowsRef = useRef<readonly GridValidRowModel[]>();
  const headerSelectionIntentRef = useRef<boolean>();
  const requestSequenceRef = useRef(0);
  const selectionScopeKeyRef = useRef(selectionScopeKey);

  if (lastExternalSelectedRowsRef.current !== enhancedGridState.selectedRows) {
    lastExternalSelectedRowsRef.current = enhancedGridState.selectedRows;
    selectedRowsRef.current = enhancedGridState.selectedRows;
  }

  useEffect(() => {
    const api = apiRef?.current;
    if (!api) return;

    return api.subscribeEvent(
      'headerSelectionCheckboxChange',
      ({ value }) => {
        headerSelectionIntentRef.current = value;
        queueMicrotask(() => {
          headerSelectionIntentRef.current = undefined;
        });
      },
      { isFirst: true }
    );
  }, [apiRef]);

  useLayoutEffect(() => {
    if (selectionScopeKeyRef.current === selectionScopeKey) return;

    selectionScopeKeyRef.current = selectionScopeKey;
    const pendingAllRows = pendingAllRowsRequestRef.current;
    const resolvedAllRows = resolvedAllRowsRef.current;
    if (pendingAllRows) {
      selectionModelRef.current = pendingAllRows.model;
      selectedRowsRef.current = pendingAllRows.rows;
    }
    pendingAllRowsRequestRef.current = undefined;
    const groupIds = new Set([...groupRowsRef.current.keys(), ...pendingGroupRequestsRef.current.keys()]);
    pendingGroupBatchesRef.current.clear();
    pendingGroupRequestsRef.current.clear();

    const ids = new Set(selectionModelRef.current.ids);
    const selectedRows = { ...selectedRowsRef.current };
    for (const groupId of groupIds) {
      ids.delete(groupId);
      const groupRows = groupRowsRef.current.get(groupId);
      if (!groupRows) continue;

      for (const rowId of groupRows.keys()) {
        ids.delete(rowId);
        delete selectedRows[rowId];
      }
    }
    for (const row of resolvedAllRows ?? []) {
      const rowId = getRowId?.(row) ?? row.id;
      ids.delete(rowId);
      delete selectedRows[rowId];
    }
    groupRowsRef.current.clear();
    resolvedAllRowsRef.current = undefined;

    const nextModel: GridRowSelectionModel = { type: 'include', ids };
    selectionModelRef.current = nextModel;
    selectedRowsRef.current = selectedRows;
    setSelectionModel(nextModel);
    enhancedGridState.updateSelectedRows(selectedRows);
  }, [enhancedGridState, getRowId, selectionScopeKey]);

  const restoreSelectionSnapshot = useCallback(
    (snapshot: SelectionSnapshot) => {
      const model: GridRowSelectionModel = { type: snapshot.model.type, ids: new Set(snapshot.model.ids) };
      const selectedRows = { ...snapshot.rows };
      selectionModelRef.current = model;
      selectedRowsRef.current = selectedRows;
      setSelectionModel(model);
      enhancedGridState.updateSelectedRows(selectedRows);
    },
    [enhancedGridState]
  );

  const rollbackPendingGroupBatches = useCallback(
    (batchIds?: ReadonlySet<number>) => {
      const idsToRollback = batchIds ?? new Set(pendingGroupBatchesRef.current.keys());
      if (idsToRollback.size === 0) return false;

      const ids = new Set(selectionModelRef.current.ids);
      const selectedRows = { ...selectedRowsRef.current };
      let changed = false;
      for (const batchId of idsToRollback) {
        const batch = pendingGroupBatchesRef.current.get(batchId);
        if (!batch) continue;

        changed = true;
        pendingGroupBatchesRef.current.delete(batchId);
        for (const groupId of batch.groupIds) {
          if (pendingGroupRequestsRef.current.get(groupId) === batchId) {
            pendingGroupRequestsRef.current.delete(groupId);
          }
          ids.delete(groupId);
          const members = groupRowsRef.current.get(groupId);
          groupRowsRef.current.delete(groupId);
          for (const rowId of members?.keys() ?? []) {
            ids.delete(rowId);
            delete selectedRows[rowId];
          }
        }
      }
      if (!changed) return false;

      const model: GridRowSelectionModel = { type: 'include', ids };
      selectionModelRef.current = model;
      selectedRowsRef.current = selectedRows;
      setSelectionModel(model);
      enhancedGridState.updateSelectedRows(selectedRows);
      return true;
    },
    [enhancedGridState]
  );

  useLayoutEffect(() => {
    const allRows = resolvedAllRowsRef.current;
    if (!allRows || !getRowGroupKey || !apiRef?.current) return;

    // Index the full-scope rows by grouping key ONCE. Scanning `allRows` inside the
    // per-visible-row loop below is O(page x scope) inside a layout effect, which blocks
    // paint — 100 rows over a 25k-product catalog is ~2.5M iterations per rows change.
    const membersByGroupKey = new Map<GridGroupNode['groupingKey'], Map<GridRowId, GridValidRowModel>>();
    for (const candidate of allRows) {
      const groupKey = getRowGroupKey(candidate);
      let keyMembers = membersByGroupKey.get(groupKey);
      if (!keyMembers) {
        keyMembers = new Map<GridRowId, GridValidRowModel>();
        membersByGroupKey.set(groupKey, keyMembers);
      }
      keyMembers.set(getRowId?.(candidate) ?? candidate.id, candidate);
    }

    const ids = new Set(selectionModelRef.current.ids);
    let changed = false;
    for (const row of rows) {
      const rowId = getRowId?.(row) ?? row.id;
      const rowNode = apiRef.current.getRowNode(rowId);
      const parentId = rowNode?.parent;
      if (parentId == null) continue;

      const groupNode = apiRef.current.getRowNode(parentId);
      if (groupNode?.type !== 'group' || !groupNode.isAutoGenerated) continue;

      const members = membersByGroupKey.get(groupNode.groupingKey) ?? new Map<GridRowId, GridValidRowModel>();
      groupRowsRef.current.set(groupNode.id, members);
      if (members.size > 0 && Array.from(members.keys()).every((memberId) => ids.has(memberId))) {
        if (!ids.has(groupNode.id)) changed = true;
        ids.add(groupNode.id);
      }
    }

    if (!changed) return;
    const model: GridRowSelectionModel = { type: 'include', ids };
    selectionModelRef.current = model;
    setSelectionModel(model);
  }, [apiRef, getRowGroupKey, getRowId, rows]);

  const handlePagedSelectionChange = useCallback<GridSelectionModelChange>(
    (incomingRowIds) => {
      let nextRowIds = incomingRowIds;
      const headerSelectionIntent = headerSelectionIntentRef.current;
      headerSelectionIntentRef.current = undefined;

      const pendingAllRows = pendingAllRowsRequestRef.current;
      if (pendingAllRows) {
        pendingAllRowsRequestRef.current = undefined;
        const pendingModel = selectionModelRef.current;
        selectionModelRef.current = pendingAllRows.model;
        selectedRowsRef.current = pendingAllRows.rows;
        if (headerSelectionIntent === undefined) {
          nextRowIds = applySelectionDelta(pendingAllRows.model, pendingModel, nextRowIds);
        }
      }

      if (headerSelectionIntent === true && resolveAllRows && nextRowIds.type === 'include') {
        rollbackPendingGroupBatches();
        const snapshot: SelectionSnapshot = { model: selectionModelRef.current, rows: selectedRowsRef.current };
        const requestId = ++requestSequenceRef.current;
        const requestScopeKey = selectionScopeKeyRef.current;
        const pendingModel: GridRowSelectionModel = { type: 'include', ids: new Set(nextRowIds.ids) };
        pendingAllRowsRequestRef.current = { ...snapshot, requestId, scopeKey: requestScopeKey };
        selectionModelRef.current = pendingModel;
        setSelectionModel(pendingModel);
        // Publish an EMPTY committed selection while the full-scope walk resolves, so a bulk
        // action cannot run against the PRE-CLICK selection; `restoreSelectionSnapshot` puts
        // the old selection back if the walk fails.
        enhancedGridState.updateSelectedRows({});

        void resolveAllRows()
          .then((allRows) => {
            const request = pendingAllRowsRequestRef.current;
            if (
              request?.requestId !== requestId ||
              request.scopeKey !== requestScopeKey ||
              selectionScopeKeyRef.current !== requestScopeKey
            ) {
              return;
            }

            pendingAllRowsRequestRef.current = undefined;
            resolvedAllRowsRef.current = allRows;
            groupRowsRef.current.clear();

            const ids = new Set(selectionModelRef.current.ids);
            const resolvedModel: GridRowSelectionModel = { type: 'include', ids };
            const selectedRows = accumulateSelectedRows(request.rows, resolvedModel, [], getRowId);
            for (const row of allRows) {
              const rowId = getRowId?.(row) ?? row.id;
              ids.add(rowId);
              selectedRows[rowId] = row;
            }

            if (getRowGroupKey) {
              // One pass over the resolved scope, not one pass per selected group node.
              const membersByGroupKey = new Map<GridGroupNode['groupingKey'], Map<GridRowId, GridValidRowModel>>();
              for (const row of allRows) {
                const groupKey = getRowGroupKey(row);
                let keyMembers = membersByGroupKey.get(groupKey);
                if (!keyMembers) {
                  keyMembers = new Map<GridRowId, GridValidRowModel>();
                  membersByGroupKey.set(groupKey, keyMembers);
                }
                keyMembers.set(getRowId?.(row) ?? row.id, row);
              }

              for (const id of nextRowIds.ids) {
                const groupNode = apiRef?.current?.getRowNode(id);
                if (groupNode?.type !== 'group' || !groupNode.isAutoGenerated) continue;

                const members = membersByGroupKey.get(groupNode.groupingKey) ?? new Map<GridRowId, GridValidRowModel>();
                groupRowsRef.current.set(groupNode.id, members);
              }
            }

            selectionModelRef.current = resolvedModel;
            selectedRowsRef.current = selectedRows;
            setSelectionModel(resolvedModel);
            enhancedGridState.updateSelectedRows(selectedRows);
          })
          .catch(() => {
            const request = pendingAllRowsRequestRef.current;
            if (request?.requestId !== requestId) return;

            pendingAllRowsRequestRef.current = undefined;
            restoreSelectionSnapshot(request);
          });
        return;
      }

      if (headerSelectionIntent === false) {
        // MUI's untick removes only the LOADED rows. The full-scope walk and every resolved group
        // added rows no page shows; they would stay selected under an unticked header.
        const dropped = new Set<string>();
        for (const row of resolvedAllRowsRef.current ?? []) dropped.add(String(getRowId?.(row) ?? row.id));
        for (const members of groupRowsRef.current.values()) {
          for (const memberId of members.keys()) dropped.add(String(memberId));
        }
        resolvedAllRowsRef.current = undefined;
        groupRowsRef.current.clear();
        if (dropped.size > 0 && nextRowIds.type === 'include') {
          nextRowIds = { type: 'include', ids: new Set([...nextRowIds.ids].filter((id) => !dropped.has(String(id)))) };
          selectedRowsRef.current = Object.fromEntries(
            Object.entries(selectedRowsRef.current).filter(([id]) => !dropped.has(id))
          );
        }
      }

      if (!resolveGroupRows || nextRowIds.type !== 'include' || selectionModelRef.current.type !== 'include') {
        selectionModelRef.current = nextRowIds;
        setSelectionModel(nextRowIds);
        if (!enableSelection) return;

        const selectedRows = accumulateSelectedRows(selectedRowsRef.current, nextRowIds, rows, getRowId);
        selectedRowsRef.current = selectedRows;
        enhancedGridState.updateSelectedRows(selectedRows);
        return;
      }

      const previousIds = selectionModelRef.current.ids;
      const nextIds = new Set(nextRowIds.ids);
      const generatedGroupNode = (id: GridRowId): GridGroupNode | undefined => {
        const node = apiRef?.current?.getRowNode(id);
        return node?.type === 'group' && node.isAutoGenerated ? node : undefined;
      };
      const newGroupNodes = Array.from(nextIds)
        .filter((id) => !previousIds.has(id))
        .map(generatedGroupNode)
        .filter((node): node is GridGroupNode => node !== undefined);

      const knownGroupIds = new Set([
        ...groupRowsRef.current.keys(),
        ...pendingGroupRequestsRef.current.keys(),
        ...newGroupNodes.map((node) => node.id),
      ]);
      const selectedRows = { ...selectedRowsRef.current };

      const cancelledBatchIds = new Set<number>();
      for (const [groupId, batchId] of pendingGroupRequestsRef.current) {
        if (previousIds.has(groupId) && !nextIds.has(groupId)) cancelledBatchIds.add(batchId);
      }
      for (const batchId of cancelledBatchIds) {
        const batch = pendingGroupBatchesRef.current.get(batchId);
        if (!batch) continue;

        pendingGroupBatchesRef.current.delete(batchId);
        for (const groupId of batch.groupIds) {
          if (pendingGroupRequestsRef.current.get(groupId) === batchId) {
            pendingGroupRequestsRef.current.delete(groupId);
          }
          nextIds.delete(groupId);
        }
      }

      for (const groupId of knownGroupIds) {
        const members = groupRowsRef.current.get(groupId);
        if (previousIds.has(groupId) && !nextIds.has(groupId)) {
          groupRowsRef.current.delete(groupId);
          if (members) {
            for (const rowId of members.keys()) {
              nextIds.delete(rowId);
              delete selectedRows[rowId];
            }
          }
          continue;
        }

        if (members && nextIds.has(groupId) && Array.from(members.keys()).some((rowId) => !nextIds.has(rowId))) {
          nextIds.delete(groupId);
          groupRowsRef.current.delete(groupId);
        }
      }

      const concreteIds = new Set(Array.from(nextIds).filter((id) => !knownGroupIds.has(id)));
      const concreteModel: GridRowSelectionModel = { type: 'include', ids: concreteIds };
      const accumulatedRows = enableSelection
        ? accumulateSelectedRows(selectedRows, concreteModel, rows, getRowId)
        : selectedRows;
      const nextModel: GridRowSelectionModel = { type: 'include', ids: nextIds };

      selectionModelRef.current = nextModel;
      selectedRowsRef.current = accumulatedRows;
      setSelectionModel(nextModel);
      if (enableSelection) {
        enhancedGridState.updateSelectedRows(accumulatedRows);
      }

      if (newGroupNodes.length > 0) {
        const batchId = ++requestSequenceRef.current;
        const requestScopeKey = selectionScopeKeyRef.current;
        const groupIds = new Set(newGroupNodes.map((node) => node.id));
        pendingGroupBatchesRef.current.set(batchId, { groupIds });
        for (const groupId of groupIds) {
          pendingGroupRequestsRef.current.set(groupId, batchId);
        }

        void Promise.all(
          newGroupNodes.map(async (groupNode) => ({ groupNode, groupRows: await resolveGroupRows(groupNode) }))
        )
          .then((resolvedGroups) => {
            const batch = pendingGroupBatchesRef.current.get(batchId);
            if (!batch || selectionScopeKeyRef.current !== requestScopeKey) return;
            if (Array.from(batch.groupIds).some((groupId) => !selectionModelRef.current.ids.has(groupId))) {
              rollbackPendingGroupBatches(new Set([batchId]));
              return;
            }

            pendingGroupBatchesRef.current.delete(batchId);
            for (const groupId of batch.groupIds) {
              if (pendingGroupRequestsRef.current.get(groupId) === batchId) {
                pendingGroupRequestsRef.current.delete(groupId);
              }
            }

            const ids = new Set(selectionModelRef.current.ids);
            const resolvedRows = { ...selectedRowsRef.current };
            for (const { groupNode, groupRows } of resolvedGroups) {
              const members = new Map<GridRowId, GridValidRowModel>();
              for (const row of groupRows) {
                const rowId = getRowId?.(row) ?? row.id;
                members.set(rowId, row);
                ids.add(rowId);
                resolvedRows[rowId] = row;
              }
              groupRowsRef.current.set(groupNode.id, members);
            }

            const resolvedModel: GridRowSelectionModel = { type: 'include', ids };
            selectionModelRef.current = resolvedModel;
            selectedRowsRef.current = resolvedRows;
            setSelectionModel(resolvedModel);
            enhancedGridState.updateSelectedRows(resolvedRows);
          })
          .catch(() => {
            if (!pendingGroupBatchesRef.current.has(batchId)) return;
            rollbackPendingGroupBatches(new Set([batchId]));
          });
      }
    },
    [
      apiRef,
      enableSelection,
      enhancedGridState,
      getRowGroupKey,
      getRowId,
      resolveAllRows,
      resolveGroupRows,
      restoreSelectionSnapshot,
      rollbackPendingGroupBatches,
      rows,
    ]
  );

  // Tree selection handler - expands parent selection to include all children
  const handleTreeSelectionChange = useCallback<GridSelectionModelChange>(
    (newSelectionModel) => {
      if (!enableTreeSelection || !getTreeDataPath) {
        // No tree selection - just update enhancedGridState normally
        const selectedRowsMap: Record<string, unknown> = {};
        const selectedIds =
          newSelectionModel.type === 'include'
            ? newSelectionModel.ids
            : new Set(
                rows.map((row) => getRowId?.(row) ?? row.id).filter((rowId) => !newSelectionModel.ids.has(rowId))
              );
        selectedIds.forEach((rowId) => {
          const row = apiRef?.current?.getRow(rowId);
          if (row) {
            selectedRowsMap[rowId] = row;
          }
        });
        enhancedGridState.updateSelectedRows(selectedRowsMap);
        return;
      }

      const resolved = resolveTreeSelection({
        previousModel: selectionModel,
        newModel: newSelectionModel,
        rows,
        getRow: (id) => apiRef?.current?.getRow(id),
        getTreeDataPath,
        getRowId,
      });

      setSelectionModel(resolved.selectionModel);
      enhancedGridState.updateSelectedRows(resolved.selectedRows);
    },
    [enableTreeSelection, getTreeDataPath, apiRef, getRowId, rows, enhancedGridState, selectionModel]
  );

  if (options.mode === 'paged') {
    return {
      checkboxSelection: enableSelection,
      disableRowSelectionExcludeModel: true,
      keepNonExistentRowsSelected: true,
      rowSelectionModel: selectionModel,
      onRowSelectionModelChange: handlePagedSelectionChange,
      rowSelectionPropagation: options.resolveGroupRows ? { parents: false, descendants: false } : undefined,
    };
  }

  return {
    checkboxSelection: enableSelection,
    disableRowSelectionExcludeModel: true,
    keepNonExistentRowsSelected: true,
    rowSelectionModel: enableTreeSelection ? selectionModel : options.rowSelectionModel,
    rowSelectionPropagation: enableTreeSelection ? { parents: false, descendants: false } : undefined,
    onRowSelectionModelChange: enableTreeSelection ? handleTreeSelectionChange : options.onRowSelectionModelChange,
  };
}
